import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";
import useFinancial from "../../Hooks/Reports/useFinancial";



const FinancialReports = ({period}) => {


  const {
    financial,
    error,
    isLoading,
    reload,
  } = useFinancial({
    period,
    detailed: true,
    include_charts: true,
  });

  if (isLoading) {
    return <p className="text-gray-600">جاري تحميل البيانات...</p>;
  }

  if (error) {
    return (
      <div className="text-center">
        <p className="text-red-500 mb-2">{error.message || "حدث خطأ ما"}</p>
        <button
          onClick={reload}
          className="px-4 py-2 bg-main1 text-light rounded-lg"
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  if (!financial) {
    return <p className="text-gray-500">لا توجد بيانات مالية متاحة</p>;
  }

  return (
    <div className="p-6 font-cairo bg-light min-h-screen">

      {/* الملخص */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <SummaryCard title="إجمالي الإيرادات" value={`${financial.summary.total_revenue.toLocaleString()}`} />
        <SummaryCard title="إجمالي العمولات" value={`${financial.summary.total_commission.toLocaleString()}`} />
        <SummaryCard title="إجمالي العمليات" value={financial.summary.total_transactions} />
        <SummaryCard title="نسبة العمولة" value={`${financial.summary.commission_rate}%`} />
      </div>

      {/* الإيرادات */}
      <div className="bg-grayish shadow rounded-2xl p-6 mb-8 animate-fadeUpSmooth">
        <h2 className="text-xl font-semibold text-main1 mb-4">مصادر الإيرادات</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={[
              {
                name: "MTN",
                revenue: financial.revenue_streams.mtn.revenue,
                commission: financial.revenue_streams.mtn.commission,
              },
              {
                name: "Syriatel",
                revenue: financial.revenue_streams.syriatel.revenue,
                commission: financial.revenue_streams.syriatel.commission,
              },
            ]}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            barCategoryGap="30%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="revenue" fill="#1C4130" radius={[6, 6, 0, 0]} name="الإيرادات" barSize={40} />
            <Bar dataKey="commission" fill="#C7882B" radius={[6, 6, 0, 0]} name="العمولات" barSize={40} />
          </BarChart>
        </ResponsiveContainer>

        {/* عرض البوكسات أسفل المخطط */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <RevenueBox
            title="MTN"
            revenue={financial.revenue_streams.mtn.revenue}
            commission={financial.revenue_streams.mtn.commission}
            transactions={financial.revenue_streams.mtn.transactions}
          />
          <RevenueBox
            title="Syriatel"
            revenue={financial.revenue_streams.syriatel.revenue}
            commission={financial.revenue_streams.syriatel.commission}
            transactions={financial.revenue_streams.syriatel.transactions}
          />
        </div>
      </div>

      {/* التفاصيل اليومية */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
  {/* التفاصيل اليومية - معاملات */}
  <div className="bg-grayish shadow rounded-2xl p-6 animate-fadeUpSmooth mb-5">
    <h2 className="text-xl font-semibold text-main1 mb-4">عدد المعاملات اليومية</h2>
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={financial.daily_breakdown}
        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        barCategoryGap="20%"
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 12 }}
          angle={0}
          textAnchor="end"
          tickFormatter={(date) => date.slice(5)}
        />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Legend verticalAlign="bottom" height={36} />
        
        <Bar
          dataKey="mtn_transactions"
          fill="#C7882B"
          name="معاملات MTN"
          radius={[4, 4, 0, 0]}
          barSize={35}
        />
        <Bar
          dataKey="syriatel_transactions"
          fill="#C03F2A"
          name="معاملات Syriatel"
          radius={[4, 4, 0, 0]}
          barSize={35}
        />
      </BarChart>
    </ResponsiveContainer>
  </div>

  {/* التفاصيل اليومية - الحجم الكلي */}
  <div className="bg-grayish shadow rounded-2xl p-6 animate-fadeUpSmooth mb-5">
    <h2 className="text-xl font-semibold text-main1 mb-4">المبالغ اليومية </h2>
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={financial.daily_breakdown}
        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        barCategoryGap="30%"
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 12 }}
          angle={0}
          textAnchor="end"
          tickFormatter={(date) => date.slice(5)}
        />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Legend verticalAlign="bottom" height={36} />
        
        <Bar
          dataKey="total_volume"
          fill="#1C4130"
          name="الحجم الكلي"
          radius={[4, 4, 0, 0]}
          barSize={45}
        />
      </BarChart>
    </ResponsiveContainer>
  </div>
</div>

      {/* الأفضل أداء */}
      <div className="bg-grayish shadow rounded-2xl p-6 animate-fadeUpSmooth">
        <h2 className="text-xl font-semibold text-main1 mb-4">أفضل المنفذين</h2>
        <div className="overflow-x-auto rounded-2xl">
          <table className="min-w-full text-sm text-main1 font-cairo">
            <thead>
              <tr className="bg-grayish text-main1">
                <th className="p-4 text-right">رقم الهاتف</th>
                <th className="p-4 text-right">الاسم</th>
                <th className="p-4 text-center">الإيرادات</th>
              </tr>
            </thead>
            <tbody>
              {financial.top_performers.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center py-16 text-gray-500">
                    لا يوجد بيانات
                  </td>
                </tr>
              ) : (
                financial.top_performers.map((perf, idx) => (
                  <tr
                    key={idx}
                    className="border-b last:border-b-0 hover:bg-grayish/60 transition"
                  >
                    <td className="p-4 text-right text-xs">{perf.user_phone}</td>
                    <td className="p-4 text-right font-medium text-main1">{perf.name}</td>
                    <td className="p-4 text-center font-bold text-main1">
                      {perf.revenue.toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

//  Component لبطاقات الملخص
const SummaryCard = ({ title, value }) => (
  <div className="bg-grayish shadow rounded-2xl p-4 flex flex-col items-center justify-center animate-fadeUpSmooth">
    <p className="text-sm text-gray-500">{title}</p>
    <h3 className="text-xl font-bold text-main1 mt-2">{value}</h3>
  </div>
);

//  Component لعرض الإيرادات والعمولات وعدد المعاملات
const RevenueBox = ({ revenue, commission, transactions, title }) => (
  <div className="bg-light rounded-2xl shadow-md p-6 flex justify-around text-center">
    <div>
      <p className="text-xl font-bold text-main1">{revenue.toLocaleString()}</p>
      <span className="text-gray-500 text-sm">إيرادات {title}</span>
    </div>
    <div>
      <p className="text-xl font-bold text-main2">{commission.toLocaleString()}</p>
      <span className="text-gray-500 text-sm">عمولة {title}</span>
    </div>
    <div>
      <p className="text-xl font-bold text-gray-500">{transactions}</p>
      <span className="text-gray-500 text-sm">عدد معاملات {title}</span>
    </div>
  </div>
);

export default FinancialReports;
